'use client';

import { useTranslations } from 'next-intl';
import { useMemo } from 'react';

import { usePathname } from '@/lib/routing';
import type { PlaceholderSection } from './types';

/**
 * The page decides which suggestions the idle field types out: someone reading
 * news is after a headline, everyone else is after a market.
 *
 * next-intl's usePathname already strips the locale prefix, so this matches
 * from the first segment.
 */
const NEWS_PATH = /^\/news(?:\/|$)/i;

export function useSearchSection(): PlaceholderSection {
  const pathname = usePathname();
  return NEWS_PATH.test(pathname ?? '') ? 'news' : 'markets';
}

/**
 * Phrases typed out in the idle placeholder for the page's section. They are queries, not slogans: typing one has to land on results, or the
 * field is advertising a dead end. Three consequences worth knowing before
 * editing `search.placeholderSuggestions`:
 *
 * - They stay English in every locale. Both searches match the English source
 *   text only — `locale` picks the language of the results, not of the index —
 *   so a translated phrase finds nothing.
 * - Matching is substring, not keywords: "Bitcoin price" hits, "price Bitcoin"
 *   does not. Short, evergreen terms survive market churn; whole questions
 *   never match.
 * - None are nicknames: those are account data, which no static list can promise.
 *
 * `scripts/check-search-placeholders.mjs` re-checks every phrase against the
 * live API.
 */
export function useSearchPlaceholders(section: PlaceholderSection): string[] {
  const t = useTranslations('search');

  return useMemo(() => {
    const raw = t.raw(`placeholderSuggestions.${section}`);
    // t.raw() hands back the key itself when a message is missing.
    return Array.isArray(raw) ? raw.filter((item): item is string => typeof item === 'string') : [];
  }, [section, t]);
}
